import React from 'react';
import DownloadableResourcesPanel from './DownloadableResourcesPanel';
import VideoThumbnail from './VideoThumbnail';
import { getAvailableDownloads } from '../api/downloadableResources';

/**
 * Normalize resources from either array format (new) or object format (legacy)
 */
const normalizeResources = (activity) => {
  if (!activity || !activity.resources) {
    return [];
  }

  // New array format
  if (Array.isArray(activity.resources)) {
    return activity.resources;
  }

  // Legacy object format
  if (typeof activity.resources === 'object') {
    const resources = [];
    const legacy = activity.resources;
    const activityId = activity.slug || activity.id || 'activity';

    // Convert legacy videos
    const videos = legacy.video || legacy.videos || [];
    videos.forEach((video, i) => {
      resources.push({
        id: video.id || `legacy-video-${i}`,
        kind: 'video',
        title: video.title || video.name || 'Video',
        source: 'youtube',
        baseUrl: video.url || video.baseUrl || '',
        affiliate: video.affiliate || { enabled: false },
        redirectId: video.redirectId || `${activityId}-redirect-video-${i}`,
        status: video.status || { isActive: true },
        meta: video.meta || {},
      });
    });

    // Convert legacy buy items (may include PDFs)
    const buy = legacy.buy || [];
    buy.forEach((item, i) => {
      const isPdf = item.url && (item.url.endsWith('.pdf') || item.url.startsWith('/worksheets/'));
      resources.push({
        id: item.id || `legacy-buy-${i}`,
        kind: isPdf ? 'download' : 'buy',
        title: item.name || item.title || 'Resource',
        source: isPdf ? 'generated' : 'amazon',
        baseUrl: item.url || '',
        affiliate: item.affiliate || { enabled: false },
        redirectId: item.redirectId || `${activityId}-redirect-buy-${i}`,
        status: item.status || { isActive: true },
        meta: item.meta || {},
      });
    });

    // Convert legacy diy items
    const diy = legacy.diy || [];
    diy.forEach((item, i) => {
      resources.push({
        id: item.id || `legacy-diy-${i}`,
        kind: 'diy',
        title: item.name || item.title || 'DIY Guide',
        source: 'other',
        baseUrl: item.url || '',
        affiliate: { enabled: false },
        redirectId: item.redirectId || `${activityId}-redirect-diy-${i}`,
        status: item.status || { isActive: true },
        meta: item.meta || {},
      });
    });

    // Convert legacy doc items
    const docs = legacy.doc || legacy.docs || [];
    docs.forEach((doc, i) => {
      resources.push({
        id: doc.id || `legacy-doc-${i}`,
        kind: 'doc',
        title: doc.name || doc.title || 'Document',
        source: 'other',
        baseUrl: doc.url || '',
        affiliate: { enabled: false },
        redirectId: doc.redirectId || `${activityId}-redirect-doc-${i}`,
        status: doc.status || { isActive: true },
        meta: doc.meta || {},
      });
    });

    return resources;
  }

  return [];
};

/**
 * ActivityResources component - displays resources for an activity
 * @param {Object} props
 * @param {Object} props.activity - Activity object with slug, title, tags, resources
 * @param {Object} [props.interventionData] - Intervention block data if applicable
 * @param {string[]} [props.studentNames] - Student names for personalization
 * @param {Array<{id: string, text: string}>} [props.targets] - Intervention targets
 */
/**
 * For 6-week intervention packs, sort so staff resources (Session Guide, Week instructions, Progress Tracker) appear first, then worksheets.
 */
function sortResourcesForIntervention(resources) {
  const staffKeywords = ['session guide', 'instruction (staff)', 'progress tracker'];
  return [...resources].sort((a, b) => {
    const aTitle = (a.title || '').toLowerCase();
    const bTitle = (b.title || '').toLowerCase();
    const aIsStaff = staffKeywords.some((k) => aTitle.includes(k));
    const bIsStaff = staffKeywords.some((k) => bTitle.includes(k));
    if (aIsStaff && !bIsStaff) return -1;
    if (!aIsStaff && bIsStaff) return 1;
    if (aIsStaff && bIsStaff) {
      // Session Guide first, then Week 1–6 Instruction, then Progress Tracker
      if (aTitle.includes('session guide')) return -1;
      if (bTitle.includes('session guide')) return 1;
      if (aTitle.includes('progress tracker')) return 1;
      if (bTitle.includes('progress tracker')) return -1;
      const aWeek = aTitle.match(/week (\d+)/);
      const bWeek = bTitle.match(/week (\d+)/);
      if (aWeek && bWeek) return parseInt(aWeek[1], 10) - parseInt(bWeek[1], 10);
      return 0;
    }
    return 0;
  });
}

const ActivityResources = ({ activity, interventionData = null, studentNames = [], targets = [] }) => {
  let normalizedResources = normalizeResources(activity);
  // Only show video and internal downloads; hide external resource links (buy/diy/doc) for now
  normalizedResources = normalizedResources.filter(
    (r) => r.kind === 'video' || (r.kind === 'download' && r.baseUrl && (r.baseUrl.endsWith('.pdf') || r.baseUrl.startsWith('/worksheets/')))
  );
  const isSixWeekIntervention = activity?.howTo?.length === 6 && (activity?.isIntervention || activity?.tags?.includes('intervention'));
  if (isSixWeekIntervention && normalizedResources.length > 0) {
    normalizedResources = sortResourcesForIntervention(normalizedResources);
  }
  const hasResources = normalizedResources.length > 0;
  const hasDownloads = activity && getAvailableDownloads(activity).length > 0;
  
  if (!hasResources && !hasDownloads) {
    return null;
  }

  const getRedirectUrl = (redirectId) => {
    const slug = activity.slug || activity.id;
    // Use Cloud Functions URL for redirect handler (deployed)
    const functionUrl = `https://europe-west3-sen-ai-v2.cloudfunctions.net/resourceRedirect?redirectId=${redirectId}&app=sen-ed&kbSlug=${slug}`;
    return functionUrl;
  };

  const renderResource = (resource) => {
    const isUnavailable = resource.status?.lastOk === false;
    const redirectUrl = resource.redirectId ? getRedirectUrl(resource.redirectId) : resource.baseUrl;
    
    // Handle static PDF files (direct download, no redirect needed)
    const isStaticPdf = resource.baseUrl && (resource.baseUrl.startsWith('/worksheets/') || resource.baseUrl.endsWith('.pdf'));
    const downloadUrl = isStaticPdf ? resource.baseUrl : redirectUrl;

    if (resource.kind === 'video') {
      const videoId = resource.baseUrl?.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/)?.[1] || resource.redirectId?.replace('redirect-', '');
      const thumbnail = resource.meta?.thumbnail || null;

      return (
        <div
          key={resource.id}
          className={`border rounded-lg p-4 ${isUnavailable ? 'opacity-50 bg-gray-100' : 'bg-white hover:shadow-md transition-shadow'}`}
        >
          <div className="flex gap-4">
            {(thumbnail || videoId) && (
              <VideoThumbnail
                src={thumbnail}
                videoId={videoId}
                alt={resource.title}
                className="w-32 h-24 object-cover rounded"
              />
            )}
            <div className="flex-1">
              <h4 className="font-semibold text-lg mb-1">{resource.title}</h4>
              {resource.meta?.channel && (
                <p className="text-sm text-gray-600 mb-2">{resource.meta.channel}</p>
              )}
              <a
                href={redirectUrl}
                target="_blank"
                rel="sponsored noopener noreferrer nofollow"
                className={`inline-block px-4 py-2 rounded ${
                  isUnavailable
                    ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
                onClick={(e) => {
                  if (isUnavailable) {
                    e.preventDefault();
                  }
                }}
                title={isUnavailable ? 'Temporarily unavailable' : 'Open video'}
              >
                Watch Video
              </a>
              {isUnavailable && (
                <p className="text-xs text-red-600 mt-2">Temporarily unavailable</p>
              )}
            </div>
          </div>
        </div>
      );
    }

    if (resource.kind === 'buy') {
      const isAffiliate = resource.affiliate?.enabled;
      
      return (
        <div
          key={resource.id}
          className={`border rounded-lg p-4 ${isUnavailable ? 'opacity-50 bg-gray-100' : 'bg-white hover:shadow-md transition-shadow'}`}
        >
          <h4 className="font-semibold mb-2">{resource.title}</h4>
          <a
            href={redirectUrl}
            target="_blank"
            rel="sponsored noopener noreferrer nofollow"
            className={`inline-block px-4 py-2 rounded ${
              isUnavailable
                ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
                : 'bg-green-600 text-white hover:bg-green-700'
            }`}
            onClick={(e) => {
              if (isUnavailable) {
                e.preventDefault();
              }
            }}
            title={isUnavailable ? 'Temporarily unavailable' : ''}
          >
            {isAffiliate ? 'Buy (Affiliate)' : 'Buy'}
          </a>
          {isUnavailable && (
            <p className="text-xs text-red-600 mt-2">Temporarily unavailable</p>
          )}
        </div>
      );
    }

    if (resource.kind === 'download' || resource.kind === 'doc') {
      const isPdf = resource.baseUrl && (resource.baseUrl.endsWith('.pdf') || resource.baseUrl.startsWith('/worksheets/'));
      
      return (
        <div
          key={resource.id}
          className={`border rounded-lg p-4 ${isUnavailable ? 'opacity-50 bg-gray-100' : 'bg-white hover:shadow-md transition-shadow'}`}
        >
          <h4 className="font-semibold mb-2">{resource.title}</h4>
          <a
            href={downloadUrl}
            target="_blank"
            rel={isStaticPdf ? 'noopener noreferrer' : 'sponsored noopener noreferrer nofollow'}
            download={isStaticPdf ? resource.title.replace(/[^a-z0-9]/gi, '_').toLowerCase() + '.pdf' : undefined}
            className={`inline-block px-4 py-2 rounded ${
              isUnavailable
                ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
                : 'bg-purple-600 text-white hover:bg-purple-700'
            }`}
            onClick={(e) => {
              if (isUnavailable) {
                e.preventDefault();
              }
            }}
            title={isUnavailable ? 'Temporarily unavailable' : isPdf ? 'Download PDF' : 'View Document'}
          >
            {isPdf ? 'Download PDF' : 'View Document'}
          </a>
          {isUnavailable && (
            <p className="text-xs text-red-600 mt-2">Temporarily unavailable</p>
          )}
        </div>
      );
    }

    if (resource.kind === 'diy') {
      return (
        <div
          key={resource.id}
          className={`border rounded-lg p-4 ${isUnavailable ? 'opacity-50 bg-gray-100' : 'bg-white hover:shadow-md transition-shadow'}`}
        >
          <h4 className="font-semibold mb-2">{resource.title}</h4>
          <a
            href={redirectUrl}
            target="_blank"
            rel="sponsored noopener noreferrer nofollow"
            className={`inline-block px-4 py-2 rounded ${
              isUnavailable
                ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
                : 'bg-purple-600 text-white hover:bg-purple-700'
            }`}
            onClick={(e) => {
              if (isUnavailable) {
                e.preventDefault();
              }
            }}
            title={isUnavailable ? 'Temporarily unavailable' : ''}
          >
            View DIY Guide
          </a>
          {isUnavailable && (
            <p className="text-xs text-red-600 mt-2">Temporarily unavailable</p>
          )}
        </div>
      );
    }

    return null;
  };

  return (
    <div className="mt-6 space-y-6">
      {/* Downloadable Resources Panel - AI-generated PDFs */}
      {hasDownloads && (
        <DownloadableResourcesPanel 
          activity={activity}
          interventionData={interventionData}
          studentNames={studentNames}
          targets={targets}
        />
      )}
      
      {/* Resources */}
      {hasResources && (
        <div>
          <h3 className="text-xl font-semibold mb-3">Resources</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {normalizedResources.map(renderResource)}
          </div>
        </div>
      )}
    </div>
  );
};

export default ActivityResources;

